// Attribution: Integrates Ketcher (EPAM Systems, Apache 2.0) as Kendraw's
// drawing engine. Ketcher upstream: https://github.com/epam/ketcher
//
// App shell routing for Ketcher mode. Renders the Ketcher Editor in the
// canvas grid area and bridges Kendraw's side panels (PropertyPanel,
// NmrPanel, StatusBar) to Ketcher's molecule + selection state. The
// top-level toolbar column is collapsed because Ketcher ships its own
// toolbox.

import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactElement,
} from 'react';
import type { AtomId, SceneStore } from '@kendraw/scene';
import { PropertyPanel } from '../PropertyPanel';
import { StatusBar } from '../StatusBar';
import { DEFAULT_TOOL_STATE } from '../ToolPalette';
import { KetcherCanvas } from './KetcherCanvas';
import { createKetcherSceneStore } from './ketcherSceneStore';

const LazyNmrPanel = lazy(() => import('@kendraw/nmr'));

interface KetcherCanvasModeProps {
  nmrOpen: boolean;
  onNmrToggle: () => void;
  nmrHeight: number;
  onNmrHeightChange: (h: number) => void;
  showProperties: boolean;
  highlightedAtomIds: Set<AtomId>;
  onHighlightAtoms: (ids: Set<AtomId>) => void;
  selectedAtomIds: AtomId[];
}

export function KetcherCanvasMode({
  nmrOpen,
  onNmrToggle,
  nmrHeight,
  onNmrHeightChange,
  showProperties,
  highlightedAtomIds,
  onHighlightAtoms,
  selectedAtomIds,
}: KetcherCanvasModeProps): ReactElement {
  // Lazy-created shim SceneStore so NmrPanel can consume Ketcher's molecule
  // stream without modification. The shim lives for the lifetime of this
  // component.
  const shimRef = useRef<ReturnType<typeof createKetcherSceneStore> | null>(null);
  if (shimRef.current === null) {
    shimRef.current = createKetcherSceneStore();
  }
  useEffect(() => {
    return () => {
      shimRef.current?.dispose();
      shimRef.current = null;
    };
  }, []);
  const shimStore: SceneStore = shimRef.current.store;

  // PropertyPanel needs a Document — serve the shim's parsed document.
  const doc = useSyncExternalStore(
    useCallback((cb) => shimStore.subscribe(cb), [shimStore]),
    useCallback(() => shimStore.getState(), [shimStore]),
  );
  const page = doc.pages[doc.activePageIndex];
  const atomCount = page ? Object.keys(page.atoms).length : 0;
  const bondCount = page ? Object.keys(page.bonds).length : 0;

  const toolState = useMemo(() => DEFAULT_TOOL_STATE, []);

  // Bidirectional highlight between Ketcher pool ids and Kendraw AtomIds
  // (UUIDs generated by parseMolV2000) needs a translation layer and is
  // deferred to Session B. For Session A, NmrPanel highlights its own
  // chart internally, and Ketcher selection stays Ketcher-local.

  return (
    <>
      {/* Ketcher ships its own toolbox; the left grid column collapses. */}
      <div style={{ gridArea: 'toolbar', overflow: 'hidden' }} />
      <div style={{ gridArea: 'canvas', overflow: 'hidden', position: 'relative' }}>
        <KetcherCanvas />
        <KetcherSidePanelTrigger
          nmrOpen={nmrOpen}
          onNmrToggle={onNmrToggle}
          showProperties={showProperties}
        />
      </div>
      <div style={{ gridArea: 'properties', overflow: 'auto' }}>
        <PropertyPanel doc={doc} visible={showProperties} />
      </div>
      {nmrOpen && (
        <div data-testid="nmr-panel" style={{ gridArea: 'nmr' }}>
          <Suspense
            fallback={
              <div
                style={{
                  height: nmrHeight,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'var(--kd-glass-bg)',
                  color: 'var(--kd-color-text-muted)',
                  fontSize: 12,
                }}
              >
                Loading NMR...
              </div>
            }
          >
            <LazyNmrPanel
              store={shimStore}
              onClose={onNmrToggle}
              height={nmrHeight}
              onHeightChange={onNmrHeightChange}
              highlightedAtomIds={highlightedAtomIds}
              onHighlightAtoms={onHighlightAtoms}
              selectedAtomIds={selectedAtomIds}
            />
          </Suspense>
        </div>
      )}
      <div style={{ gridArea: 'status' }}>
        <StatusBar
          toolState={toolState}
          zoom={1}
          atomCount={atomCount}
          bondCount={bondCount}
          selectionCount={selectedAtomIds.length}
          valenceWarnings={0}
          cursorPos={null}
        />
      </div>
    </>
  );
}

interface KetcherSidePanelTriggerProps {
  nmrOpen: boolean;
  onNmrToggle: () => void;
  showProperties: boolean;
}

function KetcherSidePanelTrigger({
  nmrOpen,
  onNmrToggle,
}: KetcherSidePanelTriggerProps): ReactElement {
  // A single floating button to toggle the NMR panel is sufficient for
  // Session A. Property panel visibility is already controlled by the
  // Kendraw header; users can reach the dedicated buttons there.
  return (
    <button
      type="button"
      data-testid="ketcher-nmr-toggle"
      onClick={onNmrToggle}
      style={{
        position: 'absolute',
        bottom: 8,
        right: 8,
        zIndex: 5,
        padding: '6px 12px',
        borderRadius: 6,
        border: '1px solid var(--kd-color-border, #888)',
        background: nmrOpen ? 'var(--kd-color-accent, #4c8)' : 'var(--kd-glass-bg, #fff)',
        color: nmrOpen ? '#fff' : 'inherit',
        cursor: 'pointer',
        fontSize: 12,
      }}
    >
      {nmrOpen ? 'Hide NMR' : 'Show NMR'}
    </button>
  );
}
